import { CV_WAREHOUSE_KEYS } from "consts";
import type { FilterValue, ExtendedJobRecord, JobPageData } from "@types";
import { getPublicLocale, type Locale } from "consts/locale";
import { getCVWarehouseJob, getJobData } from "services/jobs";

/*
 * Format job data received from CVWarehouse API
 * to match Pia structure
 */
export const formatCVWarehouseJobs = (jobs: any[], locale: Locale): ExtendedJobRecord[] =>
  jobs.map((job) => {
    const publicLocale = getPublicLocale(locale);
    let jobCity = "";

    if (job.place?.address?.city) {
      jobCity = job.place?.address?.city;
    } else if (job.place.regions && job.place.regions.length > 0 && job.place.regions[0].name) {
      jobCity = job.place.regions[0].name;
    }

    const jobShortIntro = job.attributes.find(
      (a: any) =>
        a.internalName ===
        (publicLocale === "fr" ? CV_WAREHOUSE_KEYS.INTRO_FR : CV_WAREHOUSE_KEYS.INTRO_NL),
    );
    const jobKeywords = job.attributes.find(
      (a: any) => a.internalName === CV_WAREHOUSE_KEYS.KEYWORDS,
    );
    const jobLevel = job.attributes.find((a: any) => a.internalName === CV_WAREHOUSE_KEYS.LEVEL);
    const jobAdvantages = job.attributes.find(
      (a: any) => a.internalName === CV_WAREHOUSE_KEYS.ADVANTAGES,
    );
    const jobCategory = job.attributes.find(
      (a: any) => a.internalName === CV_WAREHOUSE_KEYS.CATEGORY,
    );
    const jobBusinessLines = job.attributes.find(
      (a: any) => a.internalName === CV_WAREHOUSE_KEYS.BUSINESS_LINE,
    );
    let jobCoordinates = job.attributes.find(
      (a: any) => a.internalName === CV_WAREHOUSE_KEYS.COORDINATES,
    );
    jobCoordinates = jobCoordinates?.values?.map((c: any) => c.internalName).join(", ");

    const jobHighlighted = job.attributes.find(
      (a: any) => a.internalName === CV_WAREHOUSE_KEYS.HIGHLIGHTED,
    );

    return {
      id: job.id,
      slug: job.id,
      title: job.name.value,
      intro: job.description.value,
      ...(jobShortIntro && {
        shortIntro: jobShortIntro.values?.map((v: any) => v.name.value).join(", "),
      }),
      ...(jobKeywords && {
        keywords: jobKeywords.values?.map((v: any) => v.internalName).join(", "),
      }),
      categories: job.categories.map((category: any) => ({
        id: category.id,
        name: category.name,
      })),
      ...(job.imageUrl && {
        image: job.imageUrl,
      }),
      workschedules: job.workSchedules.map((workSchedule: any) => ({
        id: workSchedule.id,
        name: workSchedule.name,
      })),
      publicationDate: job.publicationDate,
      ...(jobLevel && {
        level: jobLevel.values?.map((v: any) => v.name.value).join(", "),
      }),
      ...(jobAdvantages && {
        advantages: jobAdvantages.values.map((a: any) => ({
          id: a.internalName,
          name: a.name.value,
        })),
      }),
      ...(jobHighlighted && {
        isHighlighted: !!jobHighlighted.values.find(
          (v: any) => v.name.value === CV_WAREHOUSE_KEYS.YES,
        ),
      }),
      ...(jobCategory && {
        isHQ: !!jobCategory.values.find(
          (v: any) =>
            v.name.value === CV_WAREHOUSE_KEYS.HQ_NL || v.name.value === CV_WAREHOUSE_KEYS.HQ_FR,
        ),
        isOffice: !!jobCategory.values.find(
          (v: any) =>
            v.name.value === CV_WAREHOUSE_KEYS.OFFICE_NL ||
            v.name.value === CV_WAREHOUSE_KEYS.OFFICE_FR,
        ),
      }),
      ...(jobBusinessLines && {
        businessLines: jobBusinessLines.values.map((b: any) => ({
          id: b.internalName,
          name: b.name.value,
        })),
      }),
      ...(jobCoordinates && {
        coordinates: {
          latitude: jobCoordinates.split(", ")[0] ?? null,
          longitude: jobCoordinates.split(", ")[1] ?? null,
        },
      }),
      city: jobCity,
      place: {
        ...(job.place.address && {
          address: {
            ...(job.place.address.street && {
              street: job.place.address.street,
            }),
            ...(job.place.address.streetNr && {
              streetNr: job.place.address.streetNr,
            }),
            ...(job.place.address.postalCode && {
              postalCode: job.place.address.postalCode,
            }),
            ...(job.place.address.city && {
              city: job.place.address.city,
            }),
          },
        }),
        ...(job.place.country && {
          country: job.place.country.name,
        }),
        ...(job.place.regions &&
          job.place.regions.length > 0 && {
            regions: job.place.regions.map((region: any) => ({
              id: region.id,
              name: region.name,
            })),
          }),
      },
      applicationUrl: job.urls.applicationUrlBranded,
      ...(job.userPublicProfile &&
        job.userPublicProfile.firstName && {
          recruiter: {
            firstName: job.userPublicProfile.firstName,
            lastName: job.userPublicProfile.lastName,
            email: job.userPublicProfile.email,
            ...(job.userPublicProfile.pictureUrl && {
              image: job.userPublicProfile.pictureUrl,
            }),
            phonenumber: job.userPublicProfile.phones,
            linkedIn: job.userPublicProfile.linkedIn,
          },
        }),
      slugs: [
        {
          locale: "nl_BE",
          value: job.id,
        },
        {
          locale: "fr_BE",
          value: job.id,
        },
      ],
      titles: [
        {
          locale: "nl_BE",
          value: job.name.value,
        },
        {
          locale: "fr_BE",
          value: job.name.value,
        },
      ],
      hasFrenchTranslation: job.availableTranslations.some((a: any) => a.code === "fr-FR"),
      hasDutchTranslation: job.availableTranslations.some((a: any) => a.code === "nl-BE"),
    };
  });

/*
 * Enrich jobs received from CVWarehouse with missing SEO data
 * that DatoCMS provices out-of-the-box
 */
export const enrichJobWithSeoData = (job: Partial<ExtendedJobRecord>) => {
  const jobIntro = job?.shortIntro ?? "";

  if (!job) return null;
  return {
    ...job,
    slugs: job.slugs,
    titles: job.titles,
    seoMetaTags: [
      {
        attributes: null,
        content: job.title,
        tag: "title",
      },
      {
        attributes: {
          property: "og:title",
          content: job.title,
        },
        content: null,
        tag: "meta",
      },
      {
        attributes: {
          property: "twitter:title",
          content: job.title,
        },
        content: null,
        tag: "meta",
      },
      {
        attributes: {
          property: "description",
          content: jobIntro,
        },
        content: null,
        tag: "meta",
      },
      {
        attributes: {
          property: "og:description",
          content: jobIntro,
        },
        content: null,
        tag: "meta",
      },
      {
        attributes: {
          property: "twitter:description",
          content: jobIntro,
        },
        content: null,
        tag: "meta",
      },
      {
        attributes: {
          property: "twitter:card",
          content: "summary",
        },
        content: null,
        tag: "meta",
      },
      ...(job.image
        ? [
            {
              attributes: {
                property: "twitter:image",
                content: job.image,
              },
              content: null,
              tag: "meta",
            },
            {
              attributes: {
                property: "og:image",
                content: job.image,
              },
              content: null,
              tag: "meta",
            },
          ]
        : []),

      {
        attributes: {
          property: "og:type",
          content: "article",
        },
        content: null,
        tag: "meta",
      },
      {
        attributes: {
          property: "og:site_name",
          content: "PIA Group",
        },
        content: null,
        tag: "meta",
      },
    ],
  };
};

/*
 * Gets array of unique CVWarehouse attributes by key
 */
export const getUniqueCVWarehouseAttributes = (jobs: any[], key: string): FilterValue[] => {
  const uniqueAttributes: string[] = [];
  jobs.forEach((job: any) => {
    job.attributes
      .filter((a: any) => a.internalName === key)
      .forEach((a: any) => {
        a.values.forEach((v: any) => {
          const { value } = v.name;

          if (value !== "" && !uniqueAttributes.includes(value)) {
            uniqueAttributes.push(value);
          }
        });
      });
  });

  const attributes: FilterValue[] = uniqueAttributes.map((attribute: string) => ({
    value: attribute,
    label: attribute,
  }));

  return attributes;
};

export const getCompleteJobData = async (
  locale: Locale,
  slug: string,
): Promise<JobPageData | null> => {
  const job = await getJobData(locale, slug);
  const cvWarehouseJob = await getCVWarehouseJob(slug, locale);
  const cvWarehouseJobFormatted = formatCVWarehouseJobs(cvWarehouseJob, locale)[0];
  if (!job || !cvWarehouseJobFormatted) {
    return null;
  }
  return {
    ...job,
    fields: {
      ...job?.fields,
      ...enrichJobWithSeoData(cvWarehouseJobFormatted),
    },
  };
};
